import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectWebp, selectLoad, selectWrapperHeight, setWrapperHeight } from '../ui/uiSlice';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { setAmplitudeUserProperties } from '../../analytics/amplitude';
import { selectIfCheckoutVisited, setLandingType, setLandingTypeB } from '../plans/plansSlice';
import { osName, osVersion, isDesktop, isIOS, isAndroid } from 'react-device-detect';
import { selectEventsData, setEventDataList } from '../events/eventsSlice';
import { useSplitFlow, queryParser } from '@applyft-web/utils';
import { selectUserUuid } from '../signup/signupSlice';
import MainContainer from '../../UI/MainContainer';
import UIButton from '../../UI/UIButton';
import { fontSize, wh } from '../../mixins';
import useWindowWidth from '../../helper/useWindowWidth';

const Title = styled.h1`
  width: 285px;
  font-weight: 600;
  ${fontSize(32)};
  line-height: 38px;
  letter-spacing: -0.005em;
  text-align: center;
  color: #323232;
  margin: 0;

  @media screen and (max-height: 540px) {
    ${fontSize(20)};
    line-height: 25px;
    width: 170px;
  }
`;

const SubTitle = styled.p`
  font-weight: 400;
  ${fontSize(16)};
  line-height: 19px;
  text-align: center;
  letter-spacing: -0.005em;
  color: #323232;
  margin-top: 8px;
  margin-bottom: 24px;

  @media screen and (max-height: 540px) {
    ${fontSize(12)};
    line-height: 16px;
    margin-top: 0;
  }
`;

const StyledWelcomeImg = styled.img`
  width: auto;
  height: 550px;
  object-fit: contain;
  margin-bottom: 50px;
  @media (max-width: 430px) or (max-height: 900px) {
    height: 500px;
  }
  @media screen and (max-height: 800px) {
    height: 400px;
  }
  @media screen and (max-height: 620px) {
    height: 300px;
  }
  @media screen and (max-height: 550px) {
    height: 200px;
    margin-bottom: 10px;
  }
  @media screen and (max-height: 360px) {
    height: 150px;
    margin-bottom: 5px;
  }
  @media screen and (max-width: 430px) {
    ${wh('100%', 'auto')}
    margin-bottom: 0px;
  }
  @media (max-width: 430px) and (max-height: 760px) {
    height: 430px;
  }
  @media (max-width: 430px) and (max-height: 610px) {
    height: 350px;
    width: auto;
  }
  @media (max-width: 430px) and (max-height: 540px) {
    height: 300px;
    margin-bottom: 10px;
  }
`;

const Welcome = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [eventParams, setEventParams] = useState(null);
  const [currentLandingType, setCurrentLandingType] = useState('fullPrice');
  const eventsData = useSelector(selectEventsData);
  const isWebp = useSelector(selectWebp);
  const load = useSelector(selectLoad);
  const userUuid = useSelector(selectUserUuid);
  const isCheckoutVisited = useSelector(selectIfCheckoutVisited);
  const wrapperHeight = useSelector(selectWrapperHeight);
  const windowWidth = useWindowWidth();
  const allowedLandingTypesB = useMemo(() => {
    return ['trialb'];
  }, []);
  const {
    location: { pathname },
  } = props;
  const landingParam = pathname.replace(/\//g, '');
  const { landingType, flowType: paywallType } = useSplitFlow(landingParam, {
    debug: process.env.REACT_APP_ENV !== 'production',
  });

  useEffect(() => {
    const handleWindowResize = () => {
      if (!wrapperHeight && !isCheckoutVisited) {
        dispatch(setWrapperHeight(window.innerHeight));
      }
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [isCheckoutVisited, wrapperHeight, dispatch]);

  useEffect(() => {
    if (eventParams || isCheckoutVisited || !paywallType) return;
    const {
      history: {
        location: { search },
      },
    } = props;

    const { fbclid, gclid, ...utms } = queryParser(search);
    const {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    } = utms;
    const facebookUtm = fbclid ? 'facebook' : null;
    const appDomain = `${window.location.origin}${landingParam.length ? '/' + landingParam : ''}`;
    const utmSource = utms['utm_source'] || facebookUtm || 'organic';
    utms['utm_source'] = utmSource;
    const isLandingTypeB = allowedLandingTypesB.includes(paywallType);
    dispatch(setLandingType(paywallType));
    setCurrentLandingType(paywallType);

    const localEventsData = {
      landing_type: `web_${landingType}`,
      app_domain: appDomain,
      app_name: 'Family-Locator',
      landingType,
      appDomain,
      appName: 'Family-Locator',
      fbclid,
      gclid,
      utm: utms,
      utm_source: utmSource,
      os: `${osVersion} ${osName}`,
    };

    const userProperties = {
      AppDomain: appDomain,
      OSDevice: `${isDesktop ? 'desktop' : isIOS ? 'iOS' : isAndroid ? 'Android' : osName}`,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      Adset_name,
      Ad_name,
      CampaignID,
      AdSetID,
      AdID,
    };

    dispatch(setLandingTypeB(isLandingTypeB));
    dispatch(setEventDataList(localEventsData));
    setAmplitudeUserProperties(userProperties);
    setEventParams(localEventsData);
  }, [
    props,
    dispatch,
    eventParams,
    userUuid,
    currentLandingType,
    allowedLandingTypesB,
    isCheckoutVisited,
    landingParam,
    landingType,
    paywallType,
  ]);

  useEffect(() => {
    if (!eventParams || isCheckoutVisited) return;
    sendAnalyticsEvents(EVENTS.onboardingStarted, eventParams);
  }, [eventParams, isCheckoutVisited]);

  const onContinueClick = () => {
    sendAnalyticsEvents(EVENTS.onboardingStartedSubmitted, eventsData);
    props.history.push(allowedLandingTypesB.includes(currentLandingType) ? '/name' : '/signup');
  };

  const imgSource =
    windowWidth >= 430 && isWebp
      ? '../assets/Welcome.webp'
      : windowWidth >= 430 && !isWebp
        ? '../assets/Welcome.png'
        : windowWidth < 430 && isWebp
          ? '../assets/mobile/Welcome.webp'
          : '../assets/mobile/Welcome.png';

  if (load) {
    return (
      <MainContainer
        desktopWidth={462}
        customPadding={'0'}
        desktopPadding={'0'}
        customTabletJustify={'flex-start'}
      >
        <StyledWelcomeImg src={imgSource} alt="Welcome" />
        <Title>{t('welcome')}</Title>
        <SubTitle>{t('welcome_sub')}</SubTitle>
        <UIButton onClick={onContinueClick} bottomPosition={'24px'} customBeforeBg={'none'}>
          {t('continue')}
        </UIButton>
      </MainContainer>
    );
  }

  return <></>;
};

export default Welcome;
